/**
 * Global single-key feature shortcuts.
 *
 *   r  Resume Editor       j  Job Manager (kanban / job board)
 *   d  Activity Dashboard  w  Writing Sample
 *   a  Application Q&A     i  Interview Q&A
 *   h  Help & Tips         ?  Help & Tips
 *
 * `HotkeyProvider` is mounted ONCE at app level (App.tsx, inside the router and
 * the HelpProvider) so the keys work on every authenticated full page. Admin
 * pages (/admin/*) are deliberately excluded.
 *
 * The keys reuse the SAME actions as the header Feature Selector — they do not
 * duplicate its logic. Two of those actions (Resume Editor, Writing Sample) are
 * Dashboard-owned modals; the Dashboard registers them through
 * `useHotkeyHandlers`, and from any other page the shortcut navigates to "/"
 * with `location.state.openFeature` set so the Dashboard opens it on arrival.
 *
 * Unlike the menu items (which `window.open()` a new tab), hotkeys navigate IN
 * the current tab.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHelp } from '../help/HelpContext';
import { openModalCount } from './modalRegistry';
import {
  FEATURE_ROUTES,
  OPEN_FEATURE_STATE_KEY,
  hasOpenModalElement,
  isHotkeyDisabledPath,
  resolveHotkey,
  type FeatureAction,
} from './hotkeyRules';

export type { FeatureAction };
export { HOTKEYS, HOTKEY_CHIP, OPEN_FEATURE_STATE_KEY } from './hotkeyRules';

/** Handlers a page can supply for the modal-backed features. */
export type HotkeyHandlers = Partial<Record<FeatureAction, () => void>>;

type HandlerRef = MutableRefObject<HotkeyHandlers>;

interface HotkeyContextValue {
  registerHandlers: (ref: HandlerRef) => () => void;
  /** Fire a feature action programmatically (same path the keys take). */
  runFeature: (action: FeatureAction) => void;
}

const HotkeyContext = createContext<HotkeyContextValue | null>(null);

/** Guard (b): module registry first (cheap), DOM sweep as the backstop. */
export function isModalOpen(): boolean {
  if (openModalCount() > 0) return true;
  return hasOpenModalElement(typeof document === 'undefined' ? null : document);
}

export function HotkeyProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { open: openHelpPanel } = useHelp();

  // Page-supplied handlers, held as refs so the keydown listener always sees
  // the latest closures without re-binding on every render.
  const handlerRefs = useRef<Set<HandlerRef>>(new Set());

  // Read inside the (memoized) action runner without re-binding on every nav.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const registerHandlers = useCallback((ref: HandlerRef) => {
    handlerRefs.current.add(ref);
    return () => {
      handlerRefs.current.delete(ref);
    };
  }, []);

  const findHandler = useCallback((action: FeatureAction): (() => void) | null => {
    for (const ref of handlerRefs.current) {
      const fn = ref.current?.[action];
      if (typeof fn === 'function') return fn;
    }
    return null;
  }, []);

  const runFeature = useCallback(
    (action: FeatureAction) => {
      if (action === 'help') {
        openHelpPanel();
        return;
      }
      // A page that owns this feature in-place (the Dashboard's modals) wins.
      const local = findHandler(action);
      if (local) {
        local();
        return;
      }
      const route = FEATURE_ROUTES[action];
      if (route) {
        // Already there: don't stack a duplicate history entry.
        if (route !== pathnameRef.current) navigate(route);
        return;
      }
      // Modal feature requested from a page that doesn't own it: go to the
      // Dashboard and let it open the modal on arrival.
      navigate('/', { state: { [OPEN_FEATURE_STATE_KEY]: action } });
    },
    [findHandler, navigate, openHelpPanel],
  );

  const disabled = isHotkeyDisabledPath(location.pathname);

  useEffect(() => {
    if (disabled) return;
    function onKeyDown(event: KeyboardEvent) {
      const action = resolveHotkey(event, { modalOpen: isModalOpen() });
      if (!action) return;
      event.preventDefault();
      runFeature(action);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [disabled, runFeature]);

  const value = useMemo<HotkeyContextValue>(
    () => ({ registerHandlers, runFeature }),
    [registerHandlers, runFeature],
  );

  return createElement(HotkeyContext.Provider, { value }, children);
}

/**
 * Register in-place handlers for features this page owns (e.g. the Dashboard's
 * Resume Editor / Writing Sample lightboxes). Safe to call outside the provider
 * (no-op) so pages stay usable in isolation.
 */
export function useHotkeyHandlers(handlers: HotkeyHandlers): void {
  const ctx = useContext(HotkeyContext);
  const ref = useRef<HotkeyHandlers>(handlers);
  ref.current = handlers;

  const registerHandlers = ctx?.registerHandlers;
  useEffect(() => {
    if (!registerHandlers) return;
    return registerHandlers(ref);
  }, [registerHandlers]);
}

/** Programmatic access to the same actions the keys trigger. */
export function useGlobalHotkeys(): HotkeyContextValue | null {
  return useContext(HotkeyContext);
}
